import { Link } from "react-router-dom";
import { useApiQuery } from "@/api/useApi";
import type { ReportSummary } from "@/api/types";
import { Icon } from "@/components/Icon";
import { ReportCard, ReportListSkeleton } from "@/components/ReportCard";
import { money } from "@/format";

const Inboxes = {
  manager: {
    path: "/api/expenses/inbox/manager",
    title: "Approvals",
    lead: "Reports from your team, and any payroll asked you to approve as an executive. Approve them, or send them back with a note.",
  },
  payroll: {
    path: "/api/expenses/inbox/payroll",
    title: "Payroll",
    lead: "Reports managers have approved. Mark each one paid with its reimbursement date, ask an executive to approve it first, or send it back.",
  },
};

export function InboxPage({ kind }: { kind: keyof typeof Inboxes }) {
  const { path, title, lead } = Inboxes[kind];
  const { data: own, error, loading } = useApiQuery<ReportSummary[]>(path);
  // Approvals also lists what payroll asked this person to approve as an executive.
  const { data: executive } = useApiQuery<ReportSummary[]>(
    kind === "manager" ? "/api/expenses/inbox/executive" : null,
  );
  const data = own && (kind === "manager" ? [...own, ...(executive ?? [])] : own);
  const reports = [...(data ?? [])].sort((a, b) =>
    (a.submittedAt ?? a.updatedAt).localeCompare(b.submittedAt ?? b.updatedAt),
  );
  const total = reports.reduce((sum, r) => sum + r.totalUsd, 0);

  return (
    <div>
      <div className="page-head">
        <h1>{title}</h1>
        {kind === "payroll" && (
          <div className="head-actions">
            <Link className="btn small secondary" to="/payroll/export">
              <Icon name="pay" size={18} /> Payroll CSV
            </Link>
            <Link className="btn small secondary" to="/payroll/gl">
              <Icon name="ledger" size={18} /> GL report
            </Link>
          </div>
        )}
      </div>
      <p className="muted lead">{lead}</p>

      {loading && !data && <ReportListSkeleton />}
      {error && (
        <div className="error" role="alert">
          {error.message}
        </div>
      )}

      {data && reports.length === 0 && (
        <div className="card empty">
          <span className="empty-icon">
            <Icon name="approve" size={28} />
          </span>
          <h2>All caught up</h2>
          <p className="muted">Nothing is waiting on you.</p>
        </div>
      )}

      {reports.length > 0 && (
        <div className="stat">
          <span className="muted">{reports.length} waiting, oldest first</span>
          <strong>{money(total)}</strong>
        </div>
      )}

      {reports.map((report) => (
        <ReportCard key={report.id} report={report} showSubmitter />
      ))}
    </div>
  );
}
